import { getPlatform, isB2B } from '@/domains/core/settings/utils';
import { useAuth } from '@/domains/customerManagement/auth/hooks/useAuth';

import {
  SellerReturnPolicyQuery,
  useSellerReturnPolicyQuery,
} from '../graphql/generated/sellerReturnPolicy.query.generated';

export type SellerReturnPolicyWithText = Omit<
  Exclude<SellerReturnPolicyQuery['sellerReturnPolicy'], undefined>,
  '__typename'
>;

export type SellerReturnPolicy = Omit<SellerReturnPolicyWithText, 'text'>;

type UseSellerReturnPolicyType = <T extends boolean>(props: {
  sellerId: number;
  withText: T;
}) => T extends true
  ? SellerReturnPolicyWithText | null
  : SellerReturnPolicy | null;

/**
 * Fetches the seller's return policy based on the provided seller ID and whether to include the text property.
 *
 * @param {number} sellerId - The unique identifier of the seller contract.
 * @param {boolean} withText - Determines whether the return policy should include the `text` property. This is used to minimize unnecessary extra computation on the ms side if `text` is not required.
 * @returns {SellerReturnPolicyWithText | null} SellerReturnPolicyWithText - If `withText` is true, returns the seller's return policy with the `text` property, or null if not found.
 * @returns {SellerReturnPolicy | null} SellerReturnPolicy - If `withText` is false, returns the seller's return policy without the `text` property, or null if not found.
 * @returns {text, customReturnPolicy, validated, returnFeesFree, returnWithdrawalLimitInDays, returnProcessTime} - The seller's return policy information.
 */
export const useSellerReturnPolicy: UseSellerReturnPolicyType = ({
  sellerId,
  withText,
}) => {
  const { userId } = useAuth();

  const [response] = useSellerReturnPolicyQuery({
    variables: {
      sellerContractId: sellerId,
      customerId: userId ?? undefined,
      b2b: isB2B(),
      platform: getPlatform(),
      text: withText,
    },
  });

  if (!response.data) return null;

  return withText
    ? (response.data.sellerReturnPolicy as SellerReturnPolicyWithText)
    : (response.data.sellerReturnPolicy as SellerReturnPolicy);
};
